import { ICoctail, TActions, TLanguage } from '../../../types';
import { Form, Tabs, message } from 'antd';
import type { TabsProps } from 'antd';
import InfoBlock from './ui/InfoBlock';
import { Button } from '../../../components';
import { Dispatch, SetStateAction, useState } from 'react';
import { useAppDispatch } from '../../../store/hooks';
import { useNavigate } from 'react-router-dom';
import { createCoctail, editCoctail } from '../../../store/coctailsSlice';
import { COCKTAILS_PAGE, formateCoctailDefaultValues, formateCoctailPayload } from '../../../lib';
import { Bounce, toast } from 'react-toastify';
import { initialCoctailData } from '../../../pages/CocktailsAddingPage/config';
import { Input } from '../../';
import { fieldPlaceholder } from './config';

const AddingInfo = ({
  coctail,
  action,
  setCoctail,
}: {
  coctail?: ICoctail;
  action: TActions;
  setCoctail: Dispatch<SetStateAction<ICoctail>>;
}) => {
  const [loading, setLoading] = useState(false);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [description, setDescription] = useState({
    eng: coctail?.eng.description,
    ru: coctail?.ru.description,
  });
  const [cookingSteps, setCookingSteps] = useState({
    eng: !!coctail?.eng?.cookingSteps
      ? JSON?.stringify(coctail?.eng?.cookingSteps, undefined, 5)
      : '',
    ru: !!coctail?.ru?.cookingSteps ? JSON?.stringify(coctail?.ru?.cookingSteps, undefined, 5) : '',
  });

  const onChangeDescription = (value: string, lang: TLanguage) => {
    setDescription(prev => ({ ...prev, [lang]: value }));
  };

  const onChangeSteps = (value: string, lang: TLanguage) => {
    setCookingSteps(prev => ({ ...prev, [lang]: value }));
  };

  const items: TabsProps['items'] = [
    {
      key: '1',
      label: 'English',
      children: (
        <InfoBlock
          lang={'eng'}
          coctail={coctail}
          changeDescription={onChangeDescription}
          changeSteps={onChangeSteps}
          setCoctail={setCoctail}
        />
      ),
    },
    {
      key: '2',
      label: 'Russian',
      children: (
        <InfoBlock
          lang={'ru'}
          coctail={coctail}
          changeDescription={onChangeDescription}
          changeSteps={onChangeSteps}
          setCoctail={setCoctail}
        />
      ),
    },
  ];

  const addCoctail = async (values: any) => {
    try {
      setLoading(true);

      const stepEng = !!cookingSteps?.eng ? JSON.parse(cookingSteps?.eng) : {};
      const stepRu = !!cookingSteps?.ru ? JSON.parse(cookingSteps?.ru) : {};

      const media = {
        posterName: coctail?.eng?.media?.posterName || '',
        photoIds: coctail?.eng?.media?.posterName
          ? [
              coctail?.eng?.media?.posterName?.slice(
                0,
                coctail?.eng?.media?.posterName.indexOf('.'),
              ),
            ]
          : [],
        videoId: coctail?.eng?.media?.videoName
          ? coctail?.eng?.media?.videoName?.slice(0, coctail?.eng?.media?.videoName.indexOf('.'))
          : '',
        videoName: coctail?.eng?.media?.videoName || '',
      };

      const formateValues: { eng: { [key: string]: any }; ru: { [key: string]: any } } =
        formateCoctailPayload(values);
      const { eng, ru, ...other } = formateValues;

      const payload = {
        ...other,
        eng: {
          ...initialCoctailData,
          ...eng,
          access: action === 'edit' ? eng.access : [eng.access],
          description: description?.eng || '',
          cookingSteps: stepEng,
          isVegan: eng.isVegan ?? false,
          baseSpirit: action === 'edit' ? eng.baseSpirit : [eng.baseSpirit],
          strength: action === 'edit' ? eng.strength : [eng.strength],
          degrees: Number(eng.degrees),
          media,
          // ingredients: eng.ingredients?.map((i: any) => ({ ...i, unit: 'ml' })),
          recommendedIds: [],
        },
        ru: {
          ...initialCoctailData,
          ...(!!ru.name ? ru : coctail.ru),
          access:
            action === 'edit' ? ru.access || coctail.ru.access : [ru.access || coctail.ru.access],
          description: description?.ru,
          cookingSteps: stepRu,
          isVegan: ru.isVegan ?? false,
          baseSpirit:
            action === 'edit'
              ? ru.baseSpirit || coctail.ru.baseSpirit
              : [ru.baseSpirit || coctail.ru.baseSpirit],
          strength:
            action === 'edit'
              ? ru.strength || coctail.ru.strength
              : [ru.strength || coctail.ru.strength],
          degrees: Number(ru.degrees || coctail.ru.degrees),
          media,
          // ingredients: ru.ingredients?.map((i: any) => ({ ...i, unit: 'мл' })),
          recommendedIds: [],
        },
      };
      if (action === 'edit') {
        const res = dispatch(editCoctail({ coctail: payload, id: coctail?._id }));

        setLoading(false);

        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        if (!!res.error) {
          console.log('[response error]', res);
          message.error('Validation Error. Please check all Languages');

          return;
        }

        message.success('Coctail edited successfully!');
      } else {
        const res = await dispatch(createCoctail(payload));

        setLoading(false);
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        if (!!res.error) {
          console.log('[response error]', res);
          message.error('Validation Error. Please check all Languages');

          return;
        }

        message.success('Coctail created successfully!');
        navigate(COCKTAILS_PAGE);
      }
    } catch (error) {
      console.error('[error]', error);
      toast.error(`Error: ${error?.message}`, {
        position: 'top-right',
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: 'light',
        transition: Bounce,
      });
      setLoading(false);
    }
  };

  const onFinishFailed = (value: unknown) => {
    console.log('[Error of validation form]', value);

    if (
      typeof value === 'object' &&
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      Array.isArray(value?.errorFields) &&
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      value?.errorFields.length
    ) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      value?.errorFields?.map(error => {
        toast.error(`Validation Error: ${error.name?.[0]}`, {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: 'light',
          transition: Bounce,
        });
      });
    }
  };

  return (
    <div>
      <Form
        style={{ maxWidth: '700px' }}
        name="add_coctail"
        autoComplete="off"
        onFinish={addCoctail}
        onFinishFailed={onFinishFailed}
        initialValues={{
          indexName: coctail.indexName,
          ...formateCoctailDefaultValues({ eng: coctail.eng, ru: coctail.ru }),
        }}
      >
        <Input
          name="indexName"
          placeholder={fieldPlaceholder.indexName['eng']}
          label={fieldPlaceholder.indexName['eng']}
          errorMessage="Required field"
          size="middle"
        />
        <Tabs defaultActiveKey="1" items={items} destroyInactiveTabPane={false} />
        <div style={{ marginTop: '20px' }}>
          <Button
            label={action === 'add' ? 'Save cocktail' : 'Edit cocktail'}
            htmlType="submit"
            bg="#000"
            color="#fff"
            loading={loading}
          />
        </div>
      </Form>
    </div>
  );
};

export default AddingInfo;
